import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import { ChevronDown, ChevronRight, Plus, Save01, SearchLg, Trash01, XCircle } from "@untitledui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { TextArea } from "@/components/base/textarea/textarea";
import { Table, TableCard } from "@/components/application/table/table";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import {
    DocumentArrangementCard,
    type ArrangementFormData,
} from "@/components/application/business-partners/document-arrangements-section";
import { useCarriers } from "@/hooks/api/use-carriers";
import {
    getHsCodeDescription,
    useHSCodes,
    useHSCodesBrowse,
    useHsCodeDescriptionLookup,
} from "@/hooks/api/use-hs-codes";
import { useMajorPorts, usePorts } from "@/hooks/api/use-ports";
import { useCompany } from "@/hooks/api/use-companies";
import {
    useCreateArrangement,
    useDeleteArrangement,
    useDocumentArrangements,
    useUpdateArrangement,
} from "@/hooks/api/use-document-arrangements";
import { useUploadDocument } from "@/hooks/api/use-documents";
import {
    useOperationalProfile,
    useUpdateOperationalProfile,
    type OperationalProfile,
    type OperationalTradeRoute,
} from "@/hooks/api/use-operational-profile";
import { toast } from "@/lib/toast-service";
import { cx } from "@/utils/cx";
import type { SelectItemType } from "@/components/base/select/select";
import type { DocumentArrangement } from "@/types/document-arrangement";

type TradeRouteDraft = OperationalTradeRoute & {
    origin_port_locode?: string;
    destination_port_locode?: string;
};

const emptyProfile = (): OperationalProfile => ({
    hs_codes: [],
    trade_routes: [],
    carrier_scacs: [],
});

function normalizeHsCode(code: string): string {
    return code.replace(/\s+/g, "").trim();
}

function serializeTradeRoutes(routes: TradeRouteDraft[]): OperationalTradeRoute[] {
    const out: OperationalTradeRoute[] = [];
    for (const r of routes) {
        const op = (r.origin_port_locode || "").trim().toUpperCase();
        const dp = (r.destination_port_locode || "").trim().toUpperCase();
        const oc = (r.origin_country_code || "").trim().toUpperCase();
        const dc = (r.destination_country_code || "").trim().toUpperCase();
        const note = r.note?.trim() || undefined;
        const routeId = r.route_id?.trim();
        if (op && dp) {
            out.push({
                ...(routeId ? { route_id: routeId } : {}),
                origin_port_locode: op,
                destination_port_locode: dp,
                ...(note ? { note } : {}),
            });
        } else if (oc && dc) {
            out.push({
                ...(routeId ? { route_id: routeId } : {}),
                origin_country_code: oc,
                destination_country_code: dc,
                ...(note ? { note } : {}),
            });
        }
    }
    return out;
}

function mapApiArrangementToForm(a: DocumentArrangement): ArrangementFormData {
    return {
        arrangement_id: a.arrangement_id,
        destination_port_id: a.destination_port_id || "",
        destination_port_name: a.destination_port_name || "",
        destination_country_code: a.destination_country_code || "",
        notify_parties: a.notify_parties || [],
        document_recipients: a.document_recipients || [],
        stakeholders: a.stakeholders || [],
        special_instructions: a.special_instructions || "",
    };
}

function defaultArrangementFromRoute(
    route: TradeRouteDraft,
    majorPorts: { un_locode: string; display_name: string; port_id: string; country_code: string }[] | undefined,
): ArrangementFormData {
    const dp = route.destination_port_locode?.trim().toUpperCase();
    const oc = route.origin_country_code?.trim().toUpperCase();
    const dc = route.destination_country_code?.trim().toUpperCase();
    if (dp) {
        const port = majorPorts?.find((p) => p.un_locode === dp);
        return {
            destination_port_id: port?.port_id ?? "",
            destination_port_name: port?.display_name ?? dp,
            destination_country_code: port?.country_code ?? "",
            notify_parties: [],
            document_recipients: [],
            stakeholders: [],
            special_instructions: "",
        };
    }
    if (oc && dc) {
        return {
            destination_port_id: "",
            destination_port_name: `${oc} → ${dc}`,
            destination_country_code: dc,
            notify_parties: [],
            document_recipients: [],
            stakeholders: [],
            special_instructions: "",
        };
    }
    return {
        destination_port_id: "",
        destination_port_name: "",
        destination_country_code: "",
        notify_parties: [],
        document_recipients: [],
        stakeholders: [],
        special_instructions: "",
    };
}

export type OperationalProfileVisibleSection = "all" | "hs" | "routes" | "carriers";

type Props = {
    companyId: string;
    readOnly: boolean;
    visibleSection?: OperationalProfileVisibleSection;
};

export function ShipperOperationalProfileSection({
    companyId,
    readOnly,
    visibleSection = "all",
}: Props) {
    const { t } = useTranslation(["pages:profile", "common"]);
    const queryClient = useQueryClient();
    const { data: company } = useCompany(companyId);
    const { data, isLoading, isError, refetch } = useOperationalProfile(companyId, true);
    const updateMutation = useUpdateOperationalProfile(companyId);
    const { data: carriersData } = useCarriers({ carrier_type: "ocean", is_active: true }, true);
    const { data: majorPortsData } = useMajorPorts(true);

    const showAll = visibleSection === "all";
    const showHs = showAll || visibleSection === "hs";
    const showRoutes = showAll || visibleSection === "routes";
    const showCarriers = showAll || visibleSection === "carriers";
    const loadArrangements = Boolean(companyId) && (showAll || visibleSection === "routes");
    const { data: arrangementsData, refetch: refetchArrangements } = useDocumentArrangements(
        undefined,
        companyId,
        loadArrangements,
    );

    const createArrangement = useCreateArrangement();
    const updateArrangement = useUpdateArrangement();
    const deleteArrangement = useDeleteArrangement();
    const uploadDocument = useUploadDocument();

    const [draft, setDraft] = useState<OperationalProfile>(emptyProfile);
    const [hsModalOpen, setHsModalOpen] = useState(false);
    const [hsModalStaging, setHsModalStaging] = useState<string[]>([]);
    const [hsModalComboKey, setHsModalComboKey] = useState(0);
    const [carrierModalOpen, setCarrierModalOpen] = useState(false);
    const [carrierModalStaging, setCarrierModalStaging] = useState<string[]>([]);
    const [carrierModalInput, setCarrierModalInput] = useState("");
    const [carrierModalComboKey, setCarrierModalComboKey] = useState(0);

    const [routeArrangements, setRouteArrangements] = useState<Record<string, ArrangementFormData>>({});
    const [expandedRouteId, setExpandedRouteId] = useState<string | null>(null);

    const [hsTableSearch, setHsTableSearch] = useState("");
    const [debouncedHsSearch] = useDebounce(hsTableSearch, 300);

    const [carrierTableSearch, setCarrierTableSearch] = useState("");
    const [debouncedCarrierSearch] = useDebounce(carrierTableSearch, 300);

    const [routesTableSearch, setRoutesTableSearch] = useState("");
    const [debouncedRoutesSearch] = useDebounce(routesTableSearch, 300);
    const [routeSort, setRouteSort] = useState<{
        key: "origin" | "destination" | "note";
        dir: "asc" | "desc";
    }>({ key: "origin", dir: "asc" });

    const { lookup: hsDescriptionLookup, isCodeLoading: isHsDescriptionLoading } = useHsCodeDescriptionLookup(
        draft.hs_codes,
        showHs,
    );

    useEffect(() => {
        if (data) {
            setDraft({
                hs_codes: [...data.hs_codes],
                trade_routes: data.trade_routes.map((r) => ({
                    route_id: r.route_id,
                    origin_port_locode: r.origin_port_locode ?? "",
                    destination_port_locode: r.destination_port_locode ?? "",
                    origin_country_code: r.origin_country_code ?? "",
                    destination_country_code: r.destination_country_code ?? "",
                    note: r.note ?? "",
                })),
                carrier_scacs: [...data.carrier_scacs],
            });
        }
    }, [data]);

    const majorList = majorPortsData?.ports;

    useEffect(() => {
        if (!draft.trade_routes.length || !majorList) return;
        setRouteArrangements((prev) => {
            const next = { ...prev };
            for (const r of draft.trade_routes) {
                const rid = r.route_id?.trim();
                if (!rid || next[rid]) continue;
                next[rid] = defaultArrangementFromRoute(r as TradeRouteDraft, majorList);
            }
            return next;
        });
    }, [draft.trade_routes, majorList]);

    useEffect(() => {
        if (!arrangementsData?.arrangements?.length) return;
        setRouteArrangements((prev) => {
            const next = { ...prev };
            for (const a of arrangementsData.arrangements) {
                if (!a.operational_route_id) continue;
                next[a.operational_route_id] = mapApiArrangementToForm(a);
            }
            return next;
        });
    }, [arrangementsData]);

    const updateRouteArrangement = useCallback((routeId: string, patch: ArrangementFormData) => {
        setRouteArrangements((prev) => ({ ...prev, [routeId]: patch }));
    }, []);

    const carrierItems: SelectItemType[] = useMemo(() => {
        if (!carriersData?.carriers?.length) return [];
        return carriersData.carriers.map((c) => ({
            id: c.scac,
            label: `${c.name} (${c.scac})`,
            textValue: `${c.name} ${c.scac} ${c.country_code ?? ""}`,
        }));
    }, [carriersData]);

    const carrierModalItemsFiltered = useMemo(() => {
        const q = carrierModalInput.trim().toLowerCase();
        if (!q) return carrierItems;
        return carrierItems.filter((item) => {
            const tv = (item.textValue ?? item.label ?? item.id ?? "").toLowerCase();
            return tv.includes(q);
        });
    }, [carrierItems, carrierModalInput]);

    const addHsToModalStaging = useCallback(
        (code: string) => {
            const v = normalizeHsCode(code);
            if (!v) return;
            if (!/^[\d.]+$/.test(v) || v.length < 4) {
                toast.error(t("companyEdit.operationalProfile.hsInvalid"));
                return;
            }
            if (draft.hs_codes.includes(v)) {
                toast.error(t("companyEdit.operationalProfile.hsAlreadyDeclared"));
                return;
            }
            setHsModalStaging((prev) => (prev.includes(v) ? prev : [...prev, v]));
            setHsModalComboKey((k) => k + 1);
        },
        [t, draft.hs_codes],
    );

    const confirmHsModal = useCallback(() => {
        if (hsModalStaging.length === 0) {
            setHsModalOpen(false);
            return;
        }
        setDraft((d) => ({
            ...d,
            hs_codes: [...new Set([...d.hs_codes, ...hsModalStaging])].sort((a, b) => a.localeCompare(b)),
        }));
        setHsModalStaging([]);
        setHsModalOpen(false);
    }, [hsModalStaging]);

    const removeHsFromStaging = useCallback((code: string) => {
        setHsModalStaging((prev) => prev.filter((c) => c !== code));
    }, []);

    const removeHs = (code: string) => {
        setDraft((d) => ({ ...d, hs_codes: d.hs_codes.filter((x) => x !== code) }));
    };

    const addRoute = () => {
        const route_id = crypto.randomUUID();
        setDraft((d) => ({
            ...d,
            trade_routes: [
                ...d.trade_routes,
                {
                    route_id,
                    origin_port_locode: "",
                    destination_port_locode: "",
                    origin_country_code: "",
                    destination_country_code: "",
                    note: "",
                },
            ],
        }));
        setRouteArrangements((prev) => ({
            ...prev,
            [route_id]: defaultArrangementFromRoute({ route_id } as TradeRouteDraft, majorList),
        }));
    };

    const updateRoute = (index: number, patch: Partial<TradeRouteDraft>) => {
        setDraft((d) => {
            const next = [...d.trade_routes];
            next[index] = { ...next[index], ...patch };
            const r = next[index] as TradeRouteDraft;
            const rid = r.route_id?.trim();
            if (rid && majorList) {
                const portDefaults = defaultArrangementFromRoute(r, majorList);
                setRouteArrangements((prev) => {
                    const cur = prev[rid];
                    return {
                        ...prev,
                        [rid]: {
                            ...portDefaults,
                            arrangement_id: cur?.arrangement_id,
                            file: cur?.file,
                            notify_parties: cur?.notify_parties ?? [],
                            document_recipients: cur?.document_recipients ?? [],
                            stakeholders: cur?.stakeholders ?? [],
                            special_instructions: cur?.special_instructions ?? "",
                        },
                    };
                });
            }
            return { ...d, trade_routes: next };
        });
    };

    const removeRoute = (index: number) => {
        setDraft((d) => {
            const removed = d.trade_routes[index];
            const rid = removed?.route_id;
            if (rid) {
                setRouteArrangements((prev) => {
                    const { [rid]: _, ...rest } = prev;
                    return rest;
                });
                if (expandedRouteId === rid) setExpandedRouteId(null);
            }
            return {
                ...d,
                trade_routes: d.trade_routes.filter((_, i) => i !== index),
            };
        });
    };

    const addCarrierToModalStaging = useCallback(
        (scac: string) => {
            const s = scac.trim().toUpperCase();
            if (!s) return;
            if (draft.carrier_scacs.includes(s)) {
                toast.error(t("companyEdit.operationalProfile.carrierAlreadyDeclared"));
                return;
            }
            setCarrierModalStaging((prev) => (prev.includes(s) ? prev : [...prev, s]));
            setCarrierModalComboKey((k) => k + 1);
            setCarrierModalInput("");
        },
        [t, draft.carrier_scacs],
    );

    const confirmCarrierModal = useCallback(() => {
        if (carrierModalStaging.length === 0) {
            setCarrierModalOpen(false);
            return;
        }
        setDraft((d) => ({
            ...d,
            carrier_scacs: [...new Set([...d.carrier_scacs, ...carrierModalStaging])].sort((a, b) =>
                a.localeCompare(b),
            ),
        }));
        setCarrierModalStaging([]);
        setCarrierModalInput("");
        setCarrierModalOpen(false);
    }, [carrierModalStaging]);

    const removeCarrierFromStaging = useCallback((scac: string) => {
        setCarrierModalStaging((prev) => prev.filter((x) => x !== scac));
    }, []);

    const removeCarrier = (scac: string) => {
        setDraft((d) => ({ ...d, carrier_scacs: d.carrier_scacs.filter((x) => x !== scac) }));
    };

    const resolvePortLabel = useCallback(
        (locode: string) => {
            const u = locode.trim().toUpperCase();
            const p = majorList?.find((x) => x.un_locode === u);
            return p?.display_name ?? u;
        },
        [majorList],
    );

    const hsRows = useMemo(() => {
        let rows = draft.hs_codes.map((code) => ({
            code,
            description: hsDescriptionLookup.get(code) ?? "—",
        }));
        const q = debouncedHsSearch.trim().toLowerCase();
        if (q) {
            rows = rows.filter(
                (r) =>
                    r.code.toLowerCase().includes(q) ||
                    r.description.toLowerCase().includes(q),
            );
        }
        rows.sort((a, b) => a.code.localeCompare(b.code));
        return rows;
    }, [draft.hs_codes, hsDescriptionLookup, debouncedHsSearch]);

    const carrierRows = useMemo(() => {
        const carriers = carriersData?.carriers ?? [];
        let rows = draft.carrier_scacs.map((scac) => {
            const c = carriers.find((x) => x.scac === scac);
            return {
                scac,
                name: c?.name ?? "—",
                country: c?.country_code ?? "—",
            };
        });
        const q = debouncedCarrierSearch.trim().toLowerCase();
        if (q) {
            rows = rows.filter(
                (r) =>
                    r.scac.toLowerCase().includes(q) ||
                    r.name.toLowerCase().includes(q) ||
                    r.country.toLowerCase().includes(q),
            );
        }
        rows.sort((a, b) => a.name.localeCompare(b.name));
        return rows;
    }, [draft.carrier_scacs, carriersData, debouncedCarrierSearch]);

    const routeRowsFilteredSorted = useMemo(() => {
        const indexed = draft.trade_routes.map((route, index) => ({ route: route as TradeRouteDraft, index }));
        const q = debouncedRoutesSearch.trim().toLowerCase();
        let list = indexed;
        if (q) {
            list = list.filter(({ route }) => {
                const o = (route.origin_port_locode || route.origin_country_code || "").toString();
                const d = (route.destination_port_locode || route.destination_country_code || "").toString();
                const n = (route.note || "").toString();
                const ol = resolvePortLabel(o);
                const dl = resolvePortLabel(d);
                return (
                    o.toLowerCase().includes(q) ||
                    d.toLowerCase().includes(q) ||
                    ol.toLowerCase().includes(q) ||
                    dl.toLowerCase().includes(q) ||
                    n.toLowerCase().includes(q)
                );
            });
        }
        const mul = routeSort.dir === "asc" ? 1 : -1;
        list = [...list].sort((a, b) => {
            const ao =
                routeSort.key === "note"
                    ? (a.route.note || "").toString()
                    : routeSort.key === "origin"
                      ? (a.route.origin_port_locode || a.route.origin_country_code || "").toString()
                      : (a.route.destination_port_locode || a.route.destination_country_code || "").toString();
            const bo =
                routeSort.key === "note"
                    ? (b.route.note || "").toString()
                    : routeSort.key === "origin"
                      ? (b.route.origin_port_locode || b.route.origin_country_code || "").toString()
                      : (b.route.destination_port_locode || b.route.destination_country_code || "").toString();
            return ao.localeCompare(bo) * mul;
        });
        return list;
    }, [draft.trade_routes, debouncedRoutesSearch, routeSort, resolvePortLabel]);

    const handleSave = async () => {
        const routes = serializeTradeRoutes(draft.trade_routes as TradeRouteDraft[]);
        const routePartial = draft.trade_routes.some((r) => {
            const op = (r.origin_port_locode || "").trim();
            const dp = (r.destination_port_locode || "").trim();
            const oc = (r.origin_country_code || "").trim();
            const dc = (r.destination_country_code || "").trim();
            if (!op && !dp && !oc && !dc) return false;
            const portsOk = Boolean(op && dp);
            const countriesOk = Boolean(oc && dc);
            return !portsOk && !countriesOk;
        });
        if (routePartial) {
            toast.error(t("companyEdit.operationalProfile.routesIncomplete"));
            return;
        }

        const companyName = company?.company_name?.trim();
        if (!companyName) {
            toast.error(t("companyEdit.operationalProfile.saveError"));
            return;
        }

        const payload: OperationalProfile = {
            hs_codes: draft.hs_codes,
            trade_routes: routes,
            carrier_scacs: draft.carrier_scacs,
        };

        try {
            await updateMutation.mutateAsync(payload);
        } catch (e) {
            toast.error((e as Error)?.message || t("companyEdit.operationalProfile.saveError"));
            return;
        }

        const refetchResult = await refetchArrangements();
        const arrangementsListAfter = refetchResult.data?.arrangements ?? arrangementsData?.arrangements ?? [];

        const keepRouteIds = new Set(
            draft.trade_routes.map((r) => r.route_id).filter(Boolean) as string[],
        );
        for (const a of arrangementsListAfter) {
            if (a.operational_route_id && !keepRouteIds.has(a.operational_route_id)) {
                try {
                    await deleteArrangement.mutateAsync(a.arrangement_id);
                } catch {
                    /* toast in hook */
                }
            }
        }

        for (const route of draft.trade_routes) {
            const rid = route.route_id?.trim();
            if (!rid) continue;
            const arr = routeArrangements[rid];
            if (!arr) continue;

            const rt = route as TradeRouteDraft;
            const isPortRoute = Boolean(rt.origin_port_locode?.trim() && rt.destination_port_locode?.trim());

            const basePayload = {
                receiver_name: companyName,
                receiver_company_id: companyId,
                operational_route_id: rid,
                notify_parties: arr.notify_parties,
                document_recipients: arr.document_recipients,
                stakeholders: arr.stakeholders,
                special_instructions: arr.special_instructions || undefined,
                destination_port_name: arr.destination_port_name || undefined,
                destination_port_id: arr.destination_port_id || undefined,
                destination_country_code: isPortRoute
                    ? arr.destination_country_code || undefined
                    : rt.destination_country_code?.trim().toUpperCase() || undefined,
            };

            let arrangementId = arr.arrangement_id;

            try {
                if (arrangementId) {
                    await updateArrangement.mutateAsync({
                        arrangementId,
                        data: basePayload,
                    });
                } else {
                    const created = await createArrangement.mutateAsync(basePayload);
                    arrangementId = created.arrangement_id;
                    updateRouteArrangement(rid, { ...arr, arrangement_id: arrangementId });
                }
            } catch {
                toast.error(t("companyEdit.operationalProfile.saveError"));
                return;
            }

            if (arr.file && arrangementId) {
                try {
                    await uploadDocument.mutateAsync({
                        file: arr.file,
                        documentType: "document_arrangement",
                        receiverCompanyId: companyId,
                        arrangementId,
                        operationalRouteId: rid,
                    });
                } catch {
                    toast.error(t("companyEdit.operationalProfile.saveError"));
                }
            }
        }

        await refetchArrangements();
        await queryClient.invalidateQueries({ queryKey: ["document-arrangements"] });
        toast.success(t("companyEdit.operationalProfile.saved"));
    };

    if (isLoading && !data) {
        return (
            <div className="rounded-lg border border-secondary bg-primary p-6">
                <p className="text-sm text-tertiary">{t("common:loading")}</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-lg border border-secondary bg-primary p-6">
                <p className="text-sm text-primary">{t("companyEdit.operationalProfile.loadError")}</p>
                <Button className="mt-2" color="secondary" size="sm" onPress={() => refetch()}>
                    {t("companyEdit.retry")}
                </Button>
            </div>
        );
    }

    const panelGap = showAll ? "space-y-8" : "space-y-6";

    const saveButton = !readOnly && (
        <div className="flex justify-end pt-2">
            <Button
                type="button"
                color="primary"
                iconLeading={Save01}
                isLoading={updateMutation.isPending}
                onPress={() => void handleSave()}
            >
                {t("companyEdit.operationalProfile.saveOperational")}
            </Button>
        </div>
    );

    const toggleRouteSort = (key: "origin" | "destination" | "note") => {
        setRouteSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
        );
    };

    return (
        <div className={cx("rounded-lg border border-secondary bg-primary p-6", panelGap)}>
            {showAll ? (
                <div>
                    <h3 className="text-lg font-semibold text-primary">{t("companyEdit.operationalProfile.title")}</h3>
                    <p className="text-tertiary text-sm mt-1">{t("companyEdit.operationalProfile.description")}</p>
                </div>
            ) : null}

            {showHs ? (
                <section className="space-y-3" aria-labelledby="op-hs-heading">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <h4 id="op-hs-heading" className="text-sm font-medium text-primary">
                            {t("companyEdit.operationalProfile.hsHeading")}
                        </h4>
                        {!readOnly && (
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                iconLeading={Plus}
                                onPress={() => {
                                    setHsModalStaging([]);
                                    setHsModalOpen(true);
                                }}
                            >
                                {t("companyEdit.operationalProfile.hsAddCodesButton")}
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-tertiary">{t("companyEdit.operationalProfile.hsTableFilterHint")}</p>
                    <Input
                        size="sm"
                        className="max-w-md"
                        icon={SearchLg}
                        placeholder={t("companyEdit.operationalProfile.hsTableSearchPlaceholder")}
                        value={hsTableSearch}
                        onChange={setHsTableSearch}
                        isDisabled={readOnly}
                    />
                    {draft.hs_codes.length === 0 ? (
                        <p className="text-sm text-tertiary">{t("companyEdit.operationalProfile.hsEmpty")}</p>
                    ) : (
                        <TableCard.Root>
                            <TableCard.Header
                                title={t("companyEdit.operationalProfile.hsTableTitle")}
                                description={`${hsRows.length} / ${draft.hs_codes.length}`}
                            />
                            <Table>
                                <Table.Header>
                                    <Table.Head
                                        id="code"
                                        label={t("companyEdit.operationalProfile.hsCodeColumn")}
                                        isRowHeader
                                    />
                                    <Table.Head id="desc" label={t("companyEdit.operationalProfile.hsDescriptionColumn")} />
                                    <Table.Head label={t("companyEdit.operationalProfile.tableActions")} />
                                </Table.Header>
                                <Table.Body>
                                    {hsRows.map((row) => (
                                        <Table.Row key={row.code}>
                                            <Table.Cell>
                                                <span className="font-mono text-sm font-medium text-primary tabular-nums">
                                                    {row.code}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary line-clamp-2">
                                                    {isHsDescriptionLoading(row.code)
                                                        ? "…"
                                                        : row.description}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {!readOnly && (
                                                    <Button
                                                        type="button"
                                                        color="tertiary"
                                                        size="sm"
                                                        iconLeading={Trash01}
                                                        aria-label={t("companyEdit.operationalProfile.removeHs", {
                                                            code: row.code,
                                                        })}
                                                        onPress={() => removeHs(row.code)}
                                                    />
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    )}
                </section>
            ) : null}

            {showRoutes ? (
                <section className="space-y-3" aria-labelledby="op-routes-heading">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h4 id="op-routes-heading" className="text-sm font-medium text-primary">
                            {t("companyEdit.operationalProfile.routesHeading")}
                        </h4>
                        {!readOnly && (
                            <Button type="button" color="secondary" size="sm" iconLeading={Plus} onPress={addRoute}>
                                {t("companyEdit.operationalProfile.addRoute")}
                            </Button>
                        )}
                    </div>
                    <Input
                        size="sm"
                        className="max-w-md"
                        icon={SearchLg}
                        placeholder={t("companyEdit.operationalProfile.routesTableSearchPlaceholder")}
                        value={routesTableSearch}
                        onChange={setRoutesTableSearch}
                        isDisabled={readOnly}
                    />
                    {draft.trade_routes.length === 0 ? (
                        <p className="text-sm text-tertiary">{t("companyEdit.operationalProfile.routesEmpty")}</p>
                    ) : (
                        <TableCard.Root>
                            <TableCard.Header
                                title={t("companyEdit.operationalProfile.routesTableTitle")}
                                description={t("companyEdit.operationalProfile.routesTableHint")}
                            />
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="border-b border-secondary bg-secondary/30 text-left text-tertiary">
                                        <tr>
                                            <th className="px-4 py-2 font-medium w-10" />
                                            <th className="px-4 py-2 font-medium">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 hover:text-primary"
                                                    onClick={() => toggleRouteSort("origin")}
                                                >
                                                    {t("companyEdit.operationalProfile.originPort")}
                                                    {routeSort.key === "origin" ? ` ${routeSort.dir === "asc" ? "↑" : "↓"}` : ""}
                                                </button>
                                            </th>
                                            <th className="px-4 py-2 font-medium">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 hover:text-primary"
                                                    onClick={() => toggleRouteSort("destination")}
                                                >
                                                    {t("companyEdit.operationalProfile.destinationPort")}
                                                    {routeSort.key === "destination" ? ` ${routeSort.dir === "asc" ? "↑" : "↓"}` : ""}
                                                </button>
                                            </th>
                                            <th className="px-4 py-2 font-medium">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 hover:text-primary"
                                                    onClick={() => toggleRouteSort("note")}
                                                >
                                                    {t("companyEdit.operationalProfile.routeNote")}
                                                    {routeSort.key === "note" ? ` ${routeSort.dir === "asc" ? "↑" : "↓"}` : ""}
                                                </button>
                                            </th>
                                            <th className="px-4 py-2 font-medium text-right">
                                                {t("companyEdit.operationalProfile.tableActions")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {routeRowsFilteredSorted.map(({ route, index }) => {
                                            const rid = route.route_id?.trim() ?? "";
                                            const legacyCountry =
                                                !route.origin_port_locode &&
                                                !route.destination_port_locode &&
                                                route.origin_country_code &&
                                                route.destination_country_code;
                                            const arr = rid ? routeArrangements[rid] : undefined;
                                            const expanded = Boolean(rid && expandedRouteId === rid);
                                            return (
                                                <Fragment key={rid || `idx-${index}`}>
                                                    <tr className="border-b border-secondary">
                                                        <td className="px-4 py-3 align-top">
                                                            {rid ? (
                                                                <button
                                                                    type="button"
                                                                    className="text-tertiary hover:text-primary"
                                                                    aria-expanded={expanded}
                                                                    onClick={() =>
                                                                        setExpandedRouteId((e) => (e === rid ? null : rid))
                                                                    }
                                                                >
                                                                    {expanded ? (
                                                                        <ChevronDown className="size-4" />
                                                                    ) : (
                                                                        <ChevronRight className="size-4" />
                                                                    )}
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                        <td className="px-4 py-3 align-top min-w-[200px]">
                                                            {legacyCountry ? (
                                                                <Input
                                                                    label={t("companyEdit.operationalProfile.origin")}
                                                                    size="sm"
                                                                    value={route.origin_country_code ?? ""}
                                                                    onChange={(v) =>
                                                                        updateRoute(index, {
                                                                            origin_country_code: v.toUpperCase().slice(0, 2),
                                                                        })
                                                                    }
                                                                    isDisabled={readOnly}
                                                                />
                                                            ) : (
                                                                <RouteRowEditor
                                                                    route={route}
                                                                    index={index}
                                                                    readOnly={readOnly}
                                                                    onChange={updateRoute}
                                                                    onRemove={removeRoute}
                                                                    compact
                                                                    hideNote
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 align-top min-w-[200px]">
                                                            {legacyCountry ? (
                                                                <Input
                                                                    label={t("companyEdit.operationalProfile.destination")}
                                                                    size="sm"
                                                                    value={route.destination_country_code ?? ""}
                                                                    onChange={(v) =>
                                                                        updateRoute(index, {
                                                                            destination_country_code: v
                                                                                .toUpperCase()
                                                                                .slice(0, 2),
                                                                        })
                                                                    }
                                                                    isDisabled={readOnly}
                                                                />
                                                            ) : (
                                                                <RouteRowEditor
                                                                    route={route}
                                                                    index={index}
                                                                    readOnly={readOnly}
                                                                    onChange={updateRoute}
                                                                    onRemove={removeRoute}
                                                                    compact
                                                                    destinationOnly
                                                                    hideNote
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 align-top max-w-xs">
                                                            {legacyCountry ? (
                                                                <p className="text-xs text-tertiary mb-1">
                                                                    {t("companyEdit.operationalProfile.legacyRouteHint", {
                                                                        origin: route.origin_country_code,
                                                                        dest: route.destination_country_code,
                                                                    })}
                                                                </p>
                                                            ) : null}
                                                            <TextArea
                                                                label=""
                                                                value={route.note ?? ""}
                                                                onChange={(v) => updateRoute(index, { note: v })}
                                                                isDisabled={readOnly}
                                                                rows={2}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 align-top text-right">
                                                            {!readOnly && (
                                                                <Button
                                                                    type="button"
                                                                    color="tertiary"
                                                                    size="sm"
                                                                    iconLeading={Trash01}
                                                                    onPress={() => removeRoute(index)}
                                                                >
                                                                    {t("companyEdit.operationalProfile.removeRoute")}
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {expanded && rid && arr ? (
                                                        <tr className="bg-secondary/20">
                                                            <td colSpan={5} className="px-4 py-4">
                                                                <DocumentArrangementCard
                                                                    arrangement={arr}
                                                                    readOnlyDestination
                                                                    hideRemoveButton
                                                                    onUpdate={(next) => updateRouteArrangement(rid, next)}
                                                                    onRemove={() => undefined}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </TableCard.Root>
                    )}
                </section>
            ) : null}

            {showCarriers ? (
                <section className="space-y-3" aria-labelledby="op-carriers-heading">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <h4 id="op-carriers-heading" className="text-sm font-medium text-primary">
                            {t("companyEdit.operationalProfile.carriersHeading")}
                        </h4>
                        {!readOnly && (
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                iconLeading={Plus}
                                onPress={() => {
                                    setCarrierModalStaging([]);
                                    setCarrierModalInput("");
                                    setCarrierModalOpen(true);
                                }}
                            >
                                {t("companyEdit.operationalProfile.carriersAddButton")}
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-tertiary">{t("companyEdit.operationalProfile.carriersTableFilterHint")}</p>
                    <Input
                        size="sm"
                        className="max-w-md"
                        icon={SearchLg}
                        placeholder={t("companyEdit.operationalProfile.carriersTableSearchPlaceholder")}
                        value={carrierTableSearch}
                        onChange={setCarrierTableSearch}
                        isDisabled={readOnly}
                    />
                    {draft.carrier_scacs.length === 0 ? (
                        <p className="text-sm text-tertiary">{t("companyEdit.operationalProfile.carriersEmpty")}</p>
                    ) : (
                        <TableCard.Root>
                            <TableCard.Header
                                title={t("companyEdit.operationalProfile.carriersTableTitle")}
                                description={`${carrierRows.length} / ${draft.carrier_scacs.length}`}
                            />
                            <Table>
                                <Table.Header>
                                    <Table.Head
                                        id="scac"
                                        label={t("companyEdit.operationalProfile.carrierColumnScac")}
                                        isRowHeader
                                    />
                                    <Table.Head id="name" label={t("companyEdit.operationalProfile.carrierColumnName")} />
                                    <Table.Head id="cc" label={t("companyEdit.operationalProfile.carrierColumnCountry")} />
                                    <Table.Head label={t("companyEdit.operationalProfile.tableActions")} />
                                </Table.Header>
                                <Table.Body>
                                    {carrierRows.map((row) => (
                                        <Table.Row key={row.scac}>
                                            <Table.Cell>
                                                <span className="font-mono text-sm font-medium">{row.scac}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-primary">{row.name}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">{row.country}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {!readOnly && (
                                                    <Button
                                                        type="button"
                                                        color="tertiary"
                                                        size="sm"
                                                        iconLeading={Trash01}
                                                        aria-label={t("companyEdit.operationalProfile.removeCarrier", {
                                                            scac: row.scac,
                                                        })}
                                                        onPress={() => removeCarrier(row.scac)}
                                                    />
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                    )}
                </section>
            ) : null}

            {saveButton}

            {!readOnly && (
                <ModalOverlay
                    isOpen={hsModalOpen}
                    onOpenChange={(open) => {
                        setHsModalOpen(open);
                        if (!open) setHsModalStaging([]);
                    }}
                >
                    <Modal>
                        <Dialog aria-label={t("companyEdit.operationalProfile.hsModalTitle")}>
                            <div className="w-full max-w-lg rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div>
                                        <h2 className="text-lg font-semibold text-primary">
                                            {t("companyEdit.operationalProfile.hsModalTitle")}
                                        </h2>
                                        <p className="text-sm text-tertiary mt-1">
                                            {t("companyEdit.operationalProfile.hsModalDescription")}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setHsModalOpen(false);
                                            setHsModalStaging([]);
                                        }}
                                        className="text-tertiary hover:text-primary shrink-0"
                                        aria-label={t("companyEdit.operationalProfile.hsModalCancel")}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>

                                <HsCodeCombo
                                    key={hsModalComboKey}
                                    onSelect={addHsToModalStaging}
                                    label={t("companyEdit.operationalProfile.hsSearchLabel")}
                                    placeholder={t("companyEdit.operationalProfile.hsSearchPlaceholder")}
                                />

                                {hsModalStaging.length > 0 ? (
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {hsModalStaging.map((code) => (
                                            <span
                                                key={code}
                                                className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm font-mono text-primary"
                                            >
                                                {code}
                                                <button
                                                    type="button"
                                                    className="text-tertiary hover:text-primary"
                                                    aria-label={t("companyEdit.operationalProfile.removeHs", { code })}
                                                    onClick={() => removeHsFromStaging(code)}
                                                >
                                                    <XCircle className="size-4" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-tertiary mt-4">
                                        {t("companyEdit.operationalProfile.hsModalStagingEmpty")}
                                    </p>
                                )}

                                <div className="flex flex-wrap justify-end gap-2 mt-6">
                                    <Button
                                        type="button"
                                        color="secondary"
                                        size="md"
                                        onPress={() => {
                                            setHsModalOpen(false);
                                            setHsModalStaging([]);
                                        }}
                                    >
                                        {t("companyEdit.operationalProfile.hsModalCancel")}
                                    </Button>
                                    <Button
                                        type="button"
                                        color="primary"
                                        size="md"
                                        onPress={confirmHsModal}
                                        isDisabled={hsModalStaging.length === 0}
                                    >
                                        {t("companyEdit.operationalProfile.hsModalConfirm")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {!readOnly && (
                <ModalOverlay
                    isOpen={carrierModalOpen}
                    onOpenChange={(open) => {
                        setCarrierModalOpen(open);
                        if (!open) {
                            setCarrierModalStaging([]);
                            setCarrierModalInput("");
                        }
                    }}
                >
                    <Modal>
                        <Dialog aria-label={t("companyEdit.operationalProfile.carrierModalTitle")}>
                            <div className="w-full max-w-lg rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div>
                                        <h2 className="text-lg font-semibold text-primary">
                                            {t("companyEdit.operationalProfile.carrierModalTitle")}
                                        </h2>
                                        <p className="text-sm text-tertiary mt-1">
                                            {t("companyEdit.operationalProfile.carrierModalDescription")}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCarrierModalOpen(false);
                                            setCarrierModalStaging([]);
                                            setCarrierModalInput("");
                                        }}
                                        className="text-tertiary hover:text-primary shrink-0"
                                        aria-label={t("companyEdit.operationalProfile.carrierModalCancel")}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>

                                <Select.ComboBox
                                    key={carrierModalComboKey}
                                    label={t("companyEdit.operationalProfile.carrierSearchLabel")}
                                    placeholder={t("companyEdit.operationalProfile.carrierSearchPlaceholder")}
                                    items={carrierModalItemsFiltered}
                                    inputValue={carrierModalInput}
                                    onInputChange={setCarrierModalInput}
                                    defaultFilter={() => true}
                                    allowsEmptyCollection
                                    shortcut={false}
                                    size="sm"
                                    selectedKey={null}
                                    onSelectionChange={(key) => {
                                        if (key) addCarrierToModalStaging(String(key));
                                    }}
                                >
                                    {(item) => (
                                        <Select.Item
                                            key={item.id}
                                            id={item.id}
                                            textValue={item.textValue ?? item.label ?? item.id}
                                        >
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select.ComboBox>

                                {carrierModalStaging.length > 0 ? (
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {carrierModalStaging.map((scac) => {
                                            const c = carriersData?.carriers?.find((x) => x.scac === scac);
                                            const label = c ? `${c.name} (${scac})` : scac;
                                            return (
                                                <span
                                                    key={scac}
                                                    className="inline-flex max-w-full items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm text-primary"
                                                >
                                                    <span className="truncate" title={label}>
                                                        {label}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="text-tertiary hover:text-primary shrink-0"
                                                        aria-label={t("companyEdit.operationalProfile.removeCarrier", {
                                                            scac,
                                                        })}
                                                        onClick={() => removeCarrierFromStaging(scac)}
                                                    >
                                                        <XCircle className="size-4" />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-tertiary mt-4">
                                        {t("companyEdit.operationalProfile.carrierModalStagingEmpty")}
                                    </p>
                                )}

                                <div className="flex flex-wrap justify-end gap-2 mt-6">
                                    <Button
                                        type="button"
                                        color="secondary"
                                        size="md"
                                        onPress={() => {
                                            setCarrierModalOpen(false);
                                            setCarrierModalStaging([]);
                                            setCarrierModalInput("");
                                        }}
                                    >
                                        {t("companyEdit.operationalProfile.carrierModalCancel")}
                                    </Button>
                                    <Button
                                        type="button"
                                        color="primary"
                                        size="md"
                                        onPress={confirmCarrierModal}
                                        isDisabled={carrierModalStaging.length === 0}
                                    >
                                        {t("companyEdit.operationalProfile.carrierModalConfirm")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
}

function HsCodeCombo({
    onSelect,
    label,
    placeholder,
}: {
    onSelect: (code: string) => void;
    label: string;
    placeholder: string;
}) {
    const [input, setInput] = useState("");
    const [debounced] = useDebounce(input, 300);
    const { data: browse } = useHSCodesBrowse(true);
    const { data: search } = useHSCodes(debounced, undefined, debounced.length >= 2);

    const items: SelectItemType[] = useMemo(() => {
        const raw =
            debounced.length < 2 ? browse : search !== undefined ? search : browse;
        return (raw ?? []).map((h) => {
            const desc = getHsCodeDescription(h);
            return {
                id: h.code,
                label: `${h.code} — ${desc.length > 90 ? `${desc.slice(0, 90)}…` : desc}`,
                textValue: `${h.code} ${desc}`,
            };
        });
    }, [debounced, search, browse]);

    return (
        <Select.ComboBox
            label={label}
            placeholder={placeholder}
            items={items}
            inputValue={input}
            onInputChange={setInput}
            defaultFilter={() => true}
            shortcut={false}
            size="sm"
            selectedKey={null}
            onSelectionChange={(key) => {
                if (key) {
                    onSelect(String(key));
                    setInput("");
                }
            }}
        >
            {(item) => (
                <Select.Item key={item.id} id={item.id} textValue={item.textValue ?? item.label ?? item.id}>
                    {item.label}
                </Select.Item>
            )}
        </Select.ComboBox>
    );
}

function PortCombo({
    label,
    valueLocode,
    onChange,
    placeholder,
    readOnly,
}: {
    label: string;
    valueLocode: string;
    placeholder: string;
    onChange: (locode: string) => void;
    readOnly: boolean;
}) {
    const [input, setInput] = useState("");
    const [debounced] = useDebounce(input, 300);
    const { data: major } = useMajorPorts(true);
    const { data: searched } = usePorts(debounced, undefined, debounced.length >= 2);

    const items: SelectItemType[] = useMemo(() => {
        const ports =
            debounced.length < 2 ? major?.ports : searched !== undefined ? searched.ports : major?.ports;
        const mapped = (ports ?? []).map((p) => ({
            id: p.un_locode,
            label: p.display_name,
            textValue: `${p.display_name} ${p.un_locode} ${p.country_code}`,
        }));
        if (valueLocode && !mapped.some((m) => m.id === valueLocode)) {
            return [{ id: valueLocode, label: valueLocode, textValue: valueLocode }, ...mapped];
        }
        return mapped;
    }, [debounced, searched, major, valueLocode]);

    const resolveLabelForLocode = useCallback(
        (locode: string) => {
            const fromItems = items.find((i) => i.id === locode)?.label;
            if (fromItems) return fromItems;
            return (
                major?.ports?.find((p) => p.un_locode === locode)?.display_name ??
                searched?.ports?.find((p) => p.un_locode === locode)?.display_name ??
                locode
            );
        },
        [items, major?.ports, searched?.ports],
    );

    useEffect(() => {
        if (!valueLocode) return;
        const resolved = resolveLabelForLocode(valueLocode);
        setInput((prev) => {
            if (prev === resolved) return prev;
            if (prev === "" || prev === valueLocode) return resolved;
            return prev;
        });
    }, [valueLocode, resolveLabelForLocode]);

    return (
        <Select.ComboBox
            label={label}
            placeholder={placeholder}
            items={items}
            inputValue={input}
            onInputChange={setInput}
            defaultFilter={() => true}
            shortcut={false}
            size="sm"
            isDisabled={readOnly}
            selectedKey={valueLocode || null}
            onSelectionChange={(key) => {
                if (key == null) {
                    onChange("");
                    setInput("");
                    return;
                }
                const id = String(key);
                onChange(id);
                setInput(resolveLabelForLocode(id));
            }}
        >
            {(item) => (
                <Select.Item key={item.id} id={item.id} textValue={item.textValue ?? item.label ?? item.id}>
                    {item.label}
                </Select.Item>
            )}
        </Select.ComboBox>
    );
}

function RouteRowEditor({
    route,
    index,
    readOnly,
    onChange,
    onRemove,
    compact,
    hideNote,
    destinationOnly,
}: {
    route: TradeRouteDraft;
    index: number;
    readOnly: boolean;
    onChange: (index: number, patch: Partial<TradeRouteDraft>) => void;
    onRemove: (index: number) => void;
    compact?: boolean;
    hideNote?: boolean;
    destinationOnly?: boolean;
}) {
    const { t } = useTranslation(["pages:profile"]);

    if (destinationOnly) {
        return (
            <PortCombo
                label={t("companyEdit.operationalProfile.destinationPort")}
                placeholder={t("companyEdit.operationalProfile.portSearchPlaceholder")}
                valueLocode={route.destination_port_locode ?? ""}
                readOnly={readOnly}
                onChange={(locode) =>
                    onChange(index, {
                        destination_port_locode: locode,
                        ...(route.origin_country_code || route.destination_country_code
                            ? { origin_country_code: "", destination_country_code: "" }
                            : {}),
                    })
                }
            />
        );
    }

    if (compact) {
        return (
            <PortCombo
                label={t("companyEdit.operationalProfile.originPort")}
                placeholder={t("companyEdit.operationalProfile.portSearchPlaceholder")}
                valueLocode={route.origin_port_locode ?? ""}
                readOnly={readOnly}
                onChange={(locode) =>
                    onChange(index, {
                        origin_port_locode: locode,
                        ...(route.origin_country_code || route.destination_country_code
                            ? { origin_country_code: "", destination_country_code: "" }
                            : {}),
                    })
                }
            />
        );
    }

    const legacyCountry =
        !route.origin_port_locode &&
        !route.destination_port_locode &&
        route.origin_country_code &&
        route.destination_country_code;

    return (
        <div className="rounded-lg border border-secondary p-4 space-y-3">
            {legacyCountry ? (
                <p className="text-xs text-tertiary">
                    {t("companyEdit.operationalProfile.legacyRouteHint", {
                        origin: route.origin_country_code,
                        dest: route.destination_country_code,
                    })}
                </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PortCombo
                    label={t("companyEdit.operationalProfile.originPort")}
                    placeholder={t("companyEdit.operationalProfile.portSearchPlaceholder")}
                    valueLocode={route.origin_port_locode ?? ""}
                    readOnly={readOnly}
                    onChange={(locode) =>
                        onChange(index, {
                            origin_port_locode: locode,
                            ...(route.origin_country_code || route.destination_country_code
                                ? { origin_country_code: "", destination_country_code: "" }
                                : {}),
                        })
                    }
                />
                <PortCombo
                    label={t("companyEdit.operationalProfile.destinationPort")}
                    placeholder={t("companyEdit.operationalProfile.portSearchPlaceholder")}
                    valueLocode={route.destination_port_locode ?? ""}
                    readOnly={readOnly}
                    onChange={(locode) =>
                        onChange(index, {
                            destination_port_locode: locode,
                            ...(route.origin_country_code || route.destination_country_code
                                ? { origin_country_code: "", destination_country_code: "" }
                                : {}),
                        })
                    }
                />
            </div>
            {!hideNote ? (
                <TextArea
                    label={t("companyEdit.operationalProfile.routeNote")}
                    value={route.note ?? ""}
                    onChange={(v) => onChange(index, { note: v })}
                    isDisabled={readOnly}
                    rows={2}
                />
            ) : null}
            {!readOnly && (
                <Button
                    type="button"
                    color="tertiary"
                    size="sm"
                    iconLeading={Trash01}
                    onPress={() => onRemove(index)}
                >
                    {t("companyEdit.operationalProfile.removeRoute")}
                </Button>
            )}
        </div>
    );
}
